import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Upload, Check, ShieldCheck, Clock, Camera, Image } from '@phosphor-icons/react';
import { toast } from 'sonner';

const KYCUploadPage = () => {
  const navigate = useNavigate();
  const { api, refreshUser, kycDocuments, user } = useAuth();
  const [loading, setLoading] = useState({});

  const documents = [
    { id: 'gst_copy', label: 'GST Certificate', description: 'Upload your GST registration certificate', icon: FileText, mandatory: true, accept: '.pdf,.jpg,.jpeg,.png,.webp' },
    { id: 'fertilizer_licence', label: 'Fertilizer Licence', description: 'Upload your fertilizer dealer licence', icon: FileText, mandatory: true, accept: '.pdf,.jpg,.jpeg,.png,.webp' },
    { id: 'pesticide_licence', label: 'Pesticide Licence', description: 'Upload your pesticide dealer licence', icon: FileText, mandatory: true, accept: '.pdf,.jpg,.jpeg,.png,.webp' },
    { id: 'shop_front_photo', label: 'Shop Front Photo', description: 'Take a clear photo of your shop front', icon: Camera, mandatory: true, accept: '.jpg,.jpeg,.png,.webp,image/*' },
    { id: 'owner_selfie', label: 'Owner Selfie Inside Shop', description: 'Take a selfie inside your shop', icon: Camera, mandatory: true, accept: '.jpg,.jpeg,.png,.webp,image/*' },
    { id: 'security_cheques', label: 'Security Cheques', description: 'Upload scanned security cheques (optional)', icon: Image, mandatory: false, accept: '.pdf,.jpg,.jpeg,.png,.webp' },
  ];

  // Each ref declared at top level to comply with Rules of Hooks
  const ref_gst = useRef(null);
  const ref_fert = useRef(null);
  const ref_pest = useRef(null);
  const ref_shop = useRef(null);
  const ref_selfie = useRef(null);
  const ref_cheque = useRef(null);
  const fileInputRefs = {
    gst_copy: ref_gst,
    fertilizer_licence: ref_fert,
    pesticide_licence: ref_pest,
    shop_front_photo: ref_shop,
    owner_selfie: ref_selfie,
    security_cheques: ref_cheque,
  };

  const getDocStatus = (docType) => {
    const doc = kycDocuments?.find(d => d.doc_type === docType);
    return doc?.status || 'not_uploaded';
  };

  const getDocInfo = (docType) => {
    return kycDocuments?.find(d => d.doc_type === docType);
  };

  const handleUpload = async (docType, file) => {
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    const allowed = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    if (!allowed.includes(ext)) {
      toast.error('File type not allowed. Use PDF, JPG, PNG, or WEBP');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);

    setLoading(prev => ({ ...prev, [docType]: true }));
    try {
      await api.post('/auth/upload-kyc', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setLoading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const mandatoryDocs = documents.filter(d => d.mandatory);
  const allMandatoryUploaded = mandatoryDocs.every(doc => getDocStatus(doc.id) !== 'not_uploaded');

  if (user?.kyc_status === 'approved') {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-semibold font-outfit text-neutral-900">KYC Approved</h2>
          <p className="text-neutral-500 mt-2">
            Your KYC verification is complete. You can now access all features.
          </p>
          <p className="text-sm text-neutral-600 mt-4">
            Dealer Code: <span className="font-semibold text-red-600">{user.dealer_code}</span>
          </p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-red-600 hover:bg-red-700 text-white"
            data-testid="go-to-dashboard"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-4 py-6">
        <div className="max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold font-outfit text-neutral-900">
            KYC Verification
          </h1>
          <p className="text-neutral-500 mt-1">
            Upload required documents for verification
          </p>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-red-600 rounded-full transition-all"
                style={{ width: `${(documents.filter(d => getDocStatus(d.id) !== 'not_uploaded').length / mandatoryDocs.length) * 100}%` }}
              />
            </div>
            <span className="text-xs text-neutral-500 whitespace-nowrap">
              {documents.filter(d => getDocStatus(d.id) !== 'not_uploaded').length}/{mandatoryDocs.length} done
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Status Banner */}
        {user?.kyc_status === 'pending' && allMandatoryUploaded && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Clock size={24} className="text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-amber-800">Verification Pending</h3>
              <p className="text-sm text-amber-700 mt-1">
                Your documents are under review. You'll be notified once verified.
              </p>
            </div>
          </div>
        )}

        {/* Document Cards */}
        {documents.map((doc) => {
          const status = getDocStatus(doc.id);
          const docInfo = getDocInfo(doc.id);
          const isLoading = loading[doc.id];
          const Icon = doc.icon;
          const isImage = docInfo?.file_extension && ['.jpg', '.jpeg', '.png', '.webp'].includes(docInfo.file_extension);

          return (
            <div
              key={doc.id}
              className="bg-white border border-neutral-200 rounded-xl p-4"
              data-testid={`kyc-doc-${doc.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    status === 'pending' ? 'bg-amber-100' :
                    status === 'approved' ? 'bg-green-100' :
                    'bg-neutral-100'
                  }`}>
                    <Icon size={20} className={
                      status === 'pending' ? 'text-amber-600' :
                      status === 'approved' ? 'text-green-600' :
                      'text-neutral-500'
                    } />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-neutral-900">{doc.label}</h3>
                      {!doc.mandatory && (
                        <Badge className="bg-neutral-100 text-neutral-500 text-[10px]">Optional</Badge>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500 mt-0.5">{doc.description}</p>
                  </div>
                </div>

                {status !== 'not_uploaded' && (
                  <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
                    status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    status === 'approved' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {status === 'pending' ? 'Pending' : status === 'approved' ? 'Approved' : 'Rejected'}
                  </span>
                )}
              </div>

              {/* Preview if image */}
              {status !== 'not_uploaded' && isImage && docInfo?.id && (
                <div className="mt-3 rounded-lg overflow-hidden border border-neutral-100 bg-neutral-50 h-32 flex items-center justify-center">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docInfo.id}/download`}
                    alt={doc.label}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              <div className="mt-4">
                <input
                  ref={fileInputRefs[doc.id]}
                  type="file"
                  accept={doc.accept}
                  onChange={(e) => handleUpload(doc.id, e.target.files[0])}
                  className="hidden"
                  data-testid={`${doc.id}-file-input`}
                />

                <Button
                  onClick={() => fileInputRefs[doc.id].current?.click()}
                  disabled={isLoading}
                  variant={status !== 'not_uploaded' ? 'outline' : 'default'}
                  className={`w-full h-11 ${
                    status === 'not_uploaded'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'border-neutral-200'
                  }`}
                  data-testid={`${doc.id}-upload-button`}
                >
                  {isLoading ? (
                    'Uploading...'
                  ) : status !== 'not_uploaded' ? (
                    <>
                      <Upload size={18} className="mr-2" />
                      Re-upload
                    </>
                  ) : (
                    <>
                      {doc.icon === Camera ? <Camera size={18} className="mr-2" /> : <Upload size={18} className="mr-2" />}
                      {doc.icon === Camera ? 'Take Photo / Upload' : 'Upload File'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}

        {/* Continue Button */}
        {allMandatoryUploaded && (
          <Button
            onClick={() => navigate('/')}
            className="w-full h-12 bg-neutral-900 hover:bg-neutral-800 text-white"
            data-testid="continue-button"
          >
            Continue to Dashboard
          </Button>
        )}
      </div>
    </div>
  );
};

export default KYCUploadPage;
